// İSG uzmanı portalı — KKD (Kişisel Koruyucu Donanım) ögesi oluştur/düzenle formu (Modal + RHF +
// Zod). api-contract §24.1: `POST /protective-equipment` (create) / `PUT /protective-equipment/{uuid}`
// (update). Alanlar: ad (zorunlu), açıklama (opsiyonel), ve düzenlemede **aktif/pasif**. **`isDefault`
// istemciden GÖNDERİLMEZ** (genel öge de düzenlenebilir/pasifleştirilebilir — rozet yalnız görünüm).
//
// Genel (bizim) varsayılan öge de bu form ile düzenlenebilir; ad/açıklama/aktiflik değişir, `isDefault`
// backend'de korunur (§24.1). Başarı sonrası katalog listesi + tip-formu seçicisi (options) invalidate.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  type ProtectiveEquipmentDto,
  Spinner,
  protectiveEquipmentApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { protectiveEquipmentKeys } from '../../lib/protectiveEquipmentKeys';

interface ProtectiveEquipmentFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  item?: ProtectiveEquipmentDto | null;
  onSuccess?: (message: string) => void;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alan backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function ProtectiveEquipmentFormModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: ProtectiveEquipmentFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(item);
  const [isActive, setIsActive] = useState(true);

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('permitTypes.catalog.ppe.validation.nameRequired')),
        description: z.string().trim().optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: item?.name ?? '',
      description: item?.description ?? '',
    },
  });

  // Düzenlemede aktiflik durumunu seed et; oluşturmada aktif başlat. `open` kapanınca sıfırlanır.
  useEffect(() => {
    if (!open) {
      return;
    }
    setIsActive(isEdit ? (item?.isActive ?? true) : true);
  }, [open, isEdit, item]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      // **`isDefault` GÖNDERİLMEZ** (§24.1) — yalnız ad/açıklama(/aktiflik).
      const base = { name: values.name.trim(), description: nullable(values.description) };
      if (item) {
        return protectiveEquipmentApi.updateProtectiveEquipment(item.uuid, { ...base, isActive });
      }
      return protectiveEquipmentApi.createProtectiveEquipment(base);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: protectiveEquipmentKeys.list() });
      queryClient.invalidateQueries({ queryKey: protectiveEquipmentKeys.options() });
      reset();
      onSuccess?.(
        item ? t('permitTypes.catalog.ppe.updated') : t('permitTypes.catalog.ppe.created'),
      );
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={
        isEdit
          ? t('permitTypes.catalog.ppe.form.edit.title')
          : t('permitTypes.catalog.ppe.form.create.title')
      }
      description={t('permitTypes.catalog.ppe.form.description')}
      size="md"
      closeLabel={t('permitTypes.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permitTypes.common.cancel')}
          </Button>
          <Button type="submit" form="ppe-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('permitTypes.common.save')} />
            ) : isEdit ? (
              t('permitTypes.common.save')
            ) : (
              t('permitTypes.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="ppe-form"
        className="flex flex-col gap-5"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="ppe-name"
          label={t('permitTypes.catalog.ppe.form.field.name')}
          error={formState.errors.name?.message}
          required
        >
          <Input id="ppe-name" invalid={Boolean(formState.errors.name)} {...register('name')} />
        </Field>

        <Field
          htmlFor="ppe-description"
          label={t('permitTypes.catalog.ppe.form.field.description')}
        >
          <Input id="ppe-description" {...register('description')} />
        </Field>

        {/* Aktif/pasif — yalnız düzenlemede (yeni öge aktif başlar; §24). Genel öge de pasifleştirilebilir. */}
        {isEdit ? (
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="flex flex-col">
              <span className="font-medium">{t('permitTypes.catalog.form.field.isActive')}</span>
              <span className="text-xs text-muted">
                {t('permitTypes.catalog.form.isActiveHint')}
              </span>
            </span>
          </label>
        ) : null}
      </form>
    </Modal>
  );
}
